import { Distributions } from "@custom-types/graphs"
import { EDGE_Z_INDEX } from "@features/builder/graph/Graph"
import {
  AppNode,
  EdgeData,
  EdgesMap,
  GroupNode,
  NodeType,
  NodesMap,
  isNotGroupNode,
} from "@features/builder/graph/types"
import { UNBOUNDED_SUPPORT_FLOOR_OFFSET } from "@features/builder/helpers/consts"
import { createNodesMap, getNestedNodeParents, getNodeLock } from "@features/builder/helpers/nodes"
import logger from "@utils/logger"
import { dfCpdToStrictRows } from "@utils/node/dfCpd"
import { getParentsList } from "@utils/node/getParentsList"
import { splitLagNodeLabel } from "@utils/node/labelValidation"
import { Edge, InternalNode, MarkerType, getOutgoers, useReactFlow } from "@xyflow/react"

export const hasEdgeCycle = ({
  target,
  source,
  nodes,
  edges,
  visited = new Set(),
}: {
  target: AppNode
  source: AppNode
  nodes: AppNode[]
  edges: Edge[]
  visited?: Set<string>
}) => {
  if (visited.has(target.id)) {
    return false
  }

  visited.add(target.id)
  for (const outgoer of getOutgoers(target, nodes, edges)) {
    if (outgoer.id === source.id) {
      return true
    }
    if (hasEdgeCycle({ target: outgoer, source, nodes, edges, visited })) {
      return true
    }
  }

  return false
}

export const createNodesEdges = (nodes: AppNode[]): Edge<EdgeData>[] => {
  const edgesMap: EdgesMap = {}
  const nodesMap = createNodesMap(nodes)

  nodes.forEach((targetNode) => {
    if (!isNotGroupNode(targetNode)) return
    const parentsIds = getParentsList(targetNode)

    const parentNodes = parentsIds
      .map((id) => nodesMap[id])
      .filter((n) => n) // why there are already non-existing nodes relations?
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      .filter(isNotGroupNode) // why nodes have relation with groups?

    parentNodes.forEach((sourceNode) => {
      const edges = createEdges(sourceNode, targetNode, nodesMap)
      edges.forEach((edge) => {
        const currentEdges = edgesMap[edge.id] ?? []
        edgesMap[edge.id] = [...currentEdges, edge]
      })
    })
  })
  const edges = createEdgesBasedOnMap(edgesMap)

  // TODO kk: it's super costly calculation to do for every node.
  // For RareEarth graph it took on my pretty fast laptop over 2 minutes.
  // Most likely we will fall back from cycle errors and just prevent user to create them (possibly remove related code).
  //
  // check for cycles
  // nodes.forEach((target) => {
  //   const sourceList = getParentsList(target).map((id) => nodesMap[id])
  //   sourceList.forEach((source) => {
  //     if (!source) return
  //     if (hasEdgeCycle({ target, source, nodes, edges })) {
  //       const invalidEdge = edges.find((e) => e.source === source.id && e.target === target.id)
  //       if (!invalidEdge) return
  //       invalidEdge.label = "Cyclic connection"
  //       invalidEdge.className = "edge-error"
  //       // @ts-expect-error tsc not recognize EdgeMarkerType color
  //       invalidEdge.markerEnd.color = "rgb(214, 0, 42)"
  //     }
  //   })
  // })
  return edges
}

// There might be a case where there are multiple visible edges with the same source and target.
// One of the cases where it would happen is connection like this:
// n1 -> n2
// n1 -> n3,
// where n2 and n3 are inside collapsed group g1. Then we want to have only 1 visible edge n1 -> g1.
// Labelled edges will be prioritise to show validation errors to user even if group is collapsed.
const createEdgesBasedOnMap = (edgesMap: EdgesMap) => {
  const edges: Edge<EdgeData>[] = []

  Object.values(edgesMap).forEach((edgesArray) => {
    //  0 or 1 edges - no need to change anything
    if (edgesArray.length < 2) {
      edges.push(...edgesArray)
      return
    }

    // at least 1 edge with label - show first labelled edge and ignore rest of them
    const firstEdgeWithLabel = edgesArray.find((e) => e.label)
    if (firstEdgeWithLabel) {
      edges.push(firstEdgeWithLabel)
      return
    }

    // multiple nodes with no labels - show one of them and ignore rest of them
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    edges.push(edgesArray[0])
  })

  return edges
}

const createEdges = (
  source: Exclude<AppNode, GroupNode>,
  target: Exclude<AppNode, GroupNode>,
  nodesMap: NodesMap,
): Edge<EdgeData>[] => {
  const edges: Edge<EdgeData>[] = []

  const allPossibleSources = [source, ...getNestedNodeParents(source, nodesMap)]
  const allPossibleTargets = [target, ...getNestedNodeParents(target, nodesMap)]

  allPossibleSources.forEach((currentSource) => {
    allPossibleTargets.forEach((currentTarget) => {
      // ignore creating edge for two connected nodes within single COLLAPSED group
      if (currentTarget.id === currentSource.id) return

      const edge = createEdge(currentSource, currentTarget, source, target, nodesMap)
      // we want to have only visible edges as reactflow won't render any edge if we have
      // 2 edges with the same id but one is visible and the other one is not
      if (!edge.hidden) edges.push(edge)
    })
  })

  return edges
}

const createEdge = (
  source: AppNode,
  target: AppNode,
  finalSource: Exclude<AppNode, GroupNode>,
  finalTarget: Exclude<AppNode, GroupNode>,
  nodesMap: NodesMap,
): Edge<EdgeData> => {
  const label = getEdgeLabel({ source: finalSource, target: finalTarget })

  const edge: Edge<EdgeData> = {
    id: `${source.id}-${target.id}`,
    source: source.id,
    target: target.id,
    label,
    type: "floating",
    className: label ? "edge-error" : "",
    zIndex: EDGE_Z_INDEX,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 15,
      height: 15,
      color: label ? "rgb(214, 0, 42)" : "#B1B1B7",
    },
    data: {
      finalSource: finalSource.id,
      finalTarget: finalTarget.id,
    },
  }

  edge.hidden = !shouldEdgeBeVisible(edge, nodesMap)

  return edge
}

export const shouldEdgeBeVisible = (edge: Edge<EdgeData>, nodesMap: NodesMap): boolean => {
  const source = nodesMap[edge.source]
  const target = nodesMap[edge.target]

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  if (source.hidden || target.hidden) return false

  const finalSource = nodesMap[edge.data!.finalSource]
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const allPossibleSources = [finalSource, ...getNestedNodeParents(finalSource, nodesMap)] // in nesting order
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const lowestVisibleSource = allPossibleSources.find((source) => !source.hidden)

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  if (lowestVisibleSource?.id !== source.id) return false

  const finalTarget = nodesMap[edge.data!.finalTarget]
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const allPossibleTargets = [finalTarget, ...getNestedNodeParents(finalTarget, nodesMap)] // in nesting order
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const lowestVisibleTarget = allPossibleTargets.find((target) => !target.hidden)

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  if (lowestVisibleTarget?.id !== target.id) return false

  return true
}

export const getEdgeLabel = ({
  source,
  target,
}: Record<"source" | "target", AppNode | InternalNode<AppNode> | undefined>) => {
  if (!source?.type || !target?.type) return

  const haveLink = "metaNode" in target.data && !!target.data.metaNode.Link
  if (haveLink) {
    return "Node with Link cannot have parent node"
  }

  if (
    target.type === NodeType.Lag &&
    "originalNode" in target.data &&
    target.data.originalNode.properties.a_sample_value
  ) {
    return "Lag node with a_sample_value cannot have parent node"
  }

  const isInvalidConnection = (invalidEdgesRules[source.type] ?? []).includes(target.type)

  return isInvalidConnection ? `${target.type} node cannot have ${source.type} parent` : undefined
}

// format: { [sourceNodeType]: [...targetsNodeTypes] }
const invalidEdgesRules: { [key: string]: string[] | undefined } = {
  [NodeType.Continuous]: [
    NodeType.Categorical,
    NodeType.Discrete,
    NodeType.Forecast,
    NodeType.Lag,
    NodeType.Scenario,
    NodeType.Table,
  ],
  [NodeType.Mixture]: [
    NodeType.Categorical,
    NodeType.Discrete,
    NodeType.Forecast,
    NodeType.Lag,
    NodeType.Scenario,
    NodeType.Table,
  ],
  [NodeType.Deterministic]: [
    NodeType.Categorical,
    NodeType.Discrete,
    NodeType.Forecast,
    NodeType.Lag,
    NodeType.Scenario,
    NodeType.Table,
  ],
  [NodeType.Discrete]: [NodeType.Forecast, NodeType.Lag, NodeType.Scenario, NodeType.Table],
  [NodeType.Categorical]: [NodeType.Forecast, NodeType.Lag, NodeType.Scenario, NodeType.Table],
  [NodeType.Table]: [
    NodeType.Categorical,
    NodeType.Continuous,
    NodeType.Discrete,
    NodeType.Forecast,
    NodeType.Lag,
    NodeType.Mixture,
    NodeType.Scenario,
    NodeType.Table,
  ],
  [NodeType.Forecast]: [
    NodeType.Categorical,
    NodeType.Discrete,
    NodeType.Forecast,
    NodeType.Lag,
    NodeType.Scenario,
    NodeType.Table,
  ],
  [NodeType.Lag]: [
    NodeType.Categorical,
    NodeType.Discrete,
    NodeType.Forecast,
    NodeType.Scenario,
    NodeType.Table,
  ],
  [NodeType.Scenario]: [
    NodeType.Categorical,
    NodeType.Discrete,
    NodeType.Forecast,
    NodeType.Lag,
    NodeType.Scenario,
    NodeType.Table,
  ],
}

export const getVisibleEdge = (
  finalSourceId: string,
  finalTargetId: string,
  allEdges: Edge<EdgeData>[],
) => {
  return allEdges.find(
    (e) => e.data?.finalSource === finalSourceId && e.data.finalTarget === finalTargetId,
  )
}

export const getEdgeLock = (
  graphId: string,
  edge: Edge<EdgeData>,
  getNode: ReturnType<typeof useReactFlow<AppNode, Edge<EdgeData>>>["getNode"],
) => {
  const { finalSource, finalTarget } = edge.data!
  const finalSourceNode = getNode(finalSource)
  const finalTargetNode = getNode(finalTarget)

  if (!finalSourceNode) {
    logger.error(`${finalSource} (finalSource of edge "${edge.id}" does not exist on graph)`)
    return
  }

  if (!finalTargetNode) {
    logger.error(`${finalTargetNode} (finalTarget of edge "${edge.id}" does not exist on graph)`)
    return
  }

  const finalSourceLock = getNodeLock(graphId, finalSourceNode.id)
  const finalTargetLock = getNodeLock(graphId, finalTargetNode.id)

  return finalSourceLock ?? finalTargetLock
}

/**
 * Child node types whose df_cpd gets one row per combination of its parents' states.
 * `Continuous` is here because it is converted to `Mixture` as soon as it gains a
 * discrete parent (see `doesNodeNeedTypeUpdate`). `Categorical` is here because a
 * discrete parent expands its table the same way (see
 * `addParentRelatedParametersToCategoricalChild`).
 */
const ENUMERATED_TARGET_TYPES: NodeType[] = [
  NodeType.Categorical,
  NodeType.Discrete,
  NodeType.Mixture,
  NodeType.Continuous,
]

const isEmptyParam = (value: string | null | undefined) =>
  value === undefined || value === null || value.trim() === ""

/**
 * Whether the source is a node with unbounded discrete support and no `ubound`,
 * connecting to a target whose rows are built from its parents' states.
 *
 * Geometric, Logseries and Poisson all have infinite support, so `ubound` is the only
 * thing that makes their state space enumerable. Without it the parent has no possible
 * values, there is nothing to build the target's rows from, and the target's df_cpd used
 * to be wiped beyond recovery.
 *
 * Scoped deliberately: Binomial is bounded by `n`, Categorical by its columns, and every
 * non-discrete parent contributes a coefficient rather than states.
 */
export const isSourceMissingUbound = ({
  source,
  target,
}: {
  source: AppNode
  target: AppNode
}): boolean => {
  if (source.type !== NodeType.Discrete) return false
  if (!ENUMERATED_TARGET_TYPES.includes(target.type)) return false

  // a linked node takes its states from the linked graph, which is not available here
  if (source.data.metaNode.Link) return false

  return dfCpdToStrictRows(source.data.originalNode.properties.df_cpd).some(
    (row) =>
      row.distribution !== undefined &&
      UNBOUNDED_SUPPORT_FLOOR_OFFSET[row.distribution as Distributions] !== undefined &&
      isEmptyParam(row.ubound),
  )
}

export const areLagsValid = ({ source, target }: { source: AppNode; target: AppNode }): boolean => {
  if (source.type !== NodeType.Lag || target.type !== NodeType.Lag) {
    return true
  }

  const { lagValue: sourceLag } = splitLagNodeLabel(source.data.label)
  const { lagValue: targetLag } = splitLagNodeLabel(target.data.label)

  return targetLag <= sourceLag
}

export const hasTargetNodeLink = (target: AppNode) =>
  "metaNode" in target.data && !!target.data.metaNode.Link

export const isGroupNode = (node: AppNode): boolean => node.type === NodeType.Group

export const isInvalidConnection = ({
  source,
  target,
}: {
  source: AppNode
  target: AppNode
}): boolean => (invalidEdgesRules[source.type] ?? []).includes(target.type)
